/**
 * sl-m82f.4.1: Shared inline styles for the analytics view.
 *
 * Kept as plain `CSSProperties` objects so every consumer (tabs, summary
 * cards, tables, drill-down) uses the same tokens. Colors come from the
 * streamlain CSS variables so they follow the app theme.
 */
import type { CSSProperties } from 'react';

export const dashboardContainer: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  marginTop: '10px',
};

export const controlBar: CSSProperties = {
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  flexWrap: 'wrap',
};

export const periodMenuTriggerStyle: CSSProperties = {
  width: '360px',
  maxWidth: '100%',
  minHeight: '46px',
  borderRadius: '6px',
  border: '1px solid var(--streamlain-border-color)',
  backgroundColor: 'var(--streamlain-background-primary-color)',
  color: 'var(--streamlain-text-primary-color)',
  fontSize: '13px',
};

export const periodMenuContentStyle: CSSProperties = {
  width: '360px',
  maxHeight: '360px',
  overflowY: 'auto',
};

export const periodMenuItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  width: '100%',
};

export const periodMenuLabelStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  minWidth: 0,
};

export const periodMenuTitleStyle: CSSProperties = {
  color: 'var(--streamlain-text-primary-color)',
  fontSize: '13px',
  lineHeight: '18px',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const periodMenuDetailStyle: CSSProperties = {
  color: 'var(--streamlain-text-secondary-color)',
  fontSize: '11px',
  lineHeight: '16px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const periodTriggerLabelStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1px',
  minWidth: 0,
};

// ─── Summary cards ──────────────────────────────────────────────────────────

export const summaryGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '12px',
};

export const summaryCard: CSSProperties = {
  padding: '14px',
  backgroundColor: 'var(--streamlain-background-secondary-color)',
  borderRadius: '8px',
  border: '1px solid var(--streamlain-border-color)',
};

export const summaryLabel: CSSProperties = {
  fontSize: '11px',
  color: 'var(--streamlain-text-secondary-color)',
  margin: '0 0 4px 0',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

export const summaryValue: CSSProperties = {
  fontSize: '22px',
  fontWeight: 600,
  color: 'var(--streamlain-text-primary-color)',
  margin: 0,
};

export const summaryValueSmall: CSSProperties = {
  ...summaryValue,
  fontSize: '14px',
  wordBreak: 'break-all',
};

export const summarySubtext: CSSProperties = {
  fontSize: '11px',
  color: 'var(--streamlain-text-secondary-color)',
  margin: '2px 0 0 0',
};

// ─── Tab bar ────────────────────────────────────────────────────────────────

export const tabBar: CSSProperties = {
  display: 'flex',
  gap: '0',
  borderBottom: '2px solid var(--streamlain-border-color)',
};

export const tabBase: CSSProperties = {
  padding: '8px 16px',
  border: 'none',
  background: 'transparent',
  color: 'var(--streamlain-text-secondary-color)',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: 500,
  borderBottom: '2px solid transparent',
  marginBottom: '-2px',
  transition: 'color 0.15s, border-color 0.15s',
};

export const tabActive: CSSProperties = {
  ...tabBase,
  color: 'var(--streamlain-primary-color)',
  borderBottomColor: 'var(--streamlain-primary-color)',
};

// ─── Tables ─────────────────────────────────────────────────────────────────

export const tableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '13px',
};

export const thStyle: CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  color: 'var(--streamlain-text-secondary-color)',
  fontWeight: 500,
  borderBottom: '1px solid var(--streamlain-border-color)',
  fontSize: '12px',
};

export const thRight: CSSProperties = { ...thStyle, textAlign: 'right' };

export const tdStyle: CSSProperties = {
  padding: '8px 10px',
  color: 'var(--streamlain-text-primary-color)',
  borderBottom: '1px solid var(--streamlain-border-color)',
};

export const tdRight: CSSProperties = { ...tdStyle, textAlign: 'right' };

export const emptyRow: CSSProperties = {
  padding: '24px 10px',
  textAlign: 'center',
  color: 'var(--streamlain-text-secondary-color)',
  fontSize: '13px',
};

export const searchInputStyle: CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid var(--streamlain-border-color)',
  backgroundColor: 'var(--streamlain-background-primary-color)',
  color: 'var(--streamlain-text-primary-color)',
  fontSize: 13,
  outline: 'none',
  marginBottom: 8,
};

// ─── Filter row (sl-m82f.4.3 drill-down) ────────────────────────────────────

export const filterRow: CSSProperties = {
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  flexWrap: 'wrap',
  marginBottom: '8px',
};

// ─── Chart ──────────────────────────────────────────────────────────────────

export const chartContainer: CSSProperties = {
  padding: '12px',
  backgroundColor: 'var(--streamlain-background-secondary-color)',
  borderRadius: '8px',
  border: '1px solid var(--streamlain-border-color)',
};

// ─── Loading / error ────────────────────────────────────────────────────────

export const loadingStyle: CSSProperties = {
  padding: '32px',
  textAlign: 'center',
  color: 'var(--streamlain-text-secondary-color)',
  fontSize: '14px',
};

export const errorStyle: CSSProperties = {
  ...loadingStyle,
  color: 'var(--streamlain-error-color)',
};
